import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Row, Col, ProgressBar, Badge, ListGroup, Card } from 'react-bootstrap';
import { BaseGatewayImage, FILE_MANAGER_API } from '@app/utils/apiUrl.js';
import axios from 'axios';
import { getToken } from '@app/utils/helper';
import { toast } from 'react-toastify';
import { acceptDoc } from '@app/utils/define';

const DocumentInput = (props) => {
    const {
        value,
        multiple,
        isInvalid,
        errorsValue,
        onChangeProp,
        onFileUploaded,
        onFilesChange,
        labelTitle = "Tải tệp tin",
        labelSubTitle,
        maxSizeMB = 20,
        accept = acceptDoc
    } = props;

    const inputUpload = useRef();
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Internal state to track files. Always an array for consistency internally.
    const [fileList, setFileList] = useState([]);

    // Sync with external value
    useEffect(() => {
        if (multiple) {
            // value should be an array or a list of objects
            if (Array.isArray(value)) {
                setFileList(value);
            } else if (value && typeof value === 'string') {
                // Compatibility or serialized data
                try {
                    const parsed = JSON.parse(value);
                    if (Array.isArray(parsed)) setFileList(parsed);
                } catch {
                    // Just one path?
                    setFileList([{ filePath: value, name: value.split('/').pop().replace(/_\d{14}(?=\.\w+$|$)/, '') }]);
                }
            }
        } else {
            // Single mode: value is likely a path string
            if (value && typeof value === 'string' && (!fileList.length || fileList[0].filePath !== value)) {
                setFileList([{ filePath: value, name: value.split('/').pop().replace(/_\d{14}(?=\.\w+$|$)/, '') }]);
            } else if (!value) {
                setFileList([]);
            }
        }
    }, [value, multiple]);

    const uploadFile = async (file) => {
        const token = await getToken();
        const config = {
            onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(percentCompleted);
            },
            headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `bearer ${token}`
            }
        };
        const data = new FormData();
        data.append('file', file);
        return axios.post(FILE_MANAGER_API.UPLOAD, data, config);
    };

    const handleUploadFile = async (event) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        setUploading(true);
        setUploadProgress(0);

        try {
            const newUploadedFiles = [];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                if (file.size / 1024 / 1024 > maxSizeMB) {
                    toast.error(`File "${file.name}" quá lớn (> ${maxSizeMB}MB)`);
                    continue;
                }

                const res = await uploadFile(file);
                if (res.data && res.data.length > 0) {
                    const fileInfo = res.data[0];
                    newUploadedFiles.push({
                        id: fileInfo.id,
                        filePath: fileInfo.filePath,
                        name: fileInfo.name || file.name
                    });

                    if (onFileUploaded) {
                        onFileUploaded(fileInfo);
                    }
                }
            }

            let updatedList;
            if (multiple) {
                updatedList = [...fileList, ...newUploadedFiles];
            } else {
                updatedList = newUploadedFiles.length > 0 ? [newUploadedFiles[0]] : fileList;
            }

            setFileList(updatedList);

            // Notify parent
            if (multiple) {
                if (onFilesChange) onFilesChange(updatedList);
                // Also update via onChangeProp if it expects a list
                if (onChangeProp) onChangeProp(updatedList);
            } else {
                const mainPath = updatedList[0]?.filePath || '';
                if (onChangeProp) onChangeProp(mainPath);
            }

            toast.success("Tải file thành công!");
        } catch (error) {
            console.error("Upload error:", error);
            toast.error("Có lỗi xảy ra khi tải file");
        } finally {
            setUploading(false);
            setUploadProgress(0);
            if (inputUpload.current) inputUpload.current.value = "";
        }
    };

    const removeFile = (index) => {
        const newList = [...fileList];
        newList.splice(index, 1);
        setFileList(newList);

        if (multiple) {
            if (onFilesChange) onFilesChange(newList);
            if (onChangeProp) onChangeProp(newList);
        } else {
            if (onChangeProp) onChangeProp('');
        }
    };

    const getFileIcon = (fileName) => {
        const ext = fileName?.split('.').pop()?.toLowerCase();
        switch (ext) {
            case 'pdf': return 'fa-file-pdf text-danger';
            case 'doc':
            case 'docx': return 'fa-file-word text-primary';
            case 'xls':
            case 'xlsx': return 'fa-file-excel text-success';
            case 'png':
            case 'jpg':
            case 'jpeg':
            case 'gif': return 'fa-file-image text-info';
            case 'zip':
            case 'rar': return 'fa-file-archive text-warning';
            default: return 'fa-file text-secondary';
        }
    };

    return (
        <div className="document-input-container">
            <Card className={`border-${isInvalid ? 'danger' : 'light'} shadow-sm`}>
                <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="font-weight-bold mb-0 text-muted small uppercase">{labelTitle}</label>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => inputUpload.current.click()}
                            disabled={uploading}
                        >
                            <i className="fas fa-cloud-upload-alt mr-1"></i> Chọn tệp...
                        </button>
                    </div>

                    {labelSubTitle && (
                        <div className="mb-2">
                            {labelSubTitle}
                        </div>
                    )}

                    <input
                        type="file"
                        hidden
                        multiple={multiple}
                        accept={accept}
                        ref={inputUpload}
                        onChange={handleUploadFile}
                    />

                    {uploading && (
                        <div className="mb-2">
                            <ProgressBar animated now={uploadProgress} label={`${uploadProgress}%`} variant="success" h="10px" />
                            <small className="text-muted mt-1 d-block text-center">Đang tải lên...</small>
                        </div>
                    )}

                    {fileList.length === 0 && !uploading && (
                        <div className="text-center py-3 border rounded bg-light" style={{ borderStyle: 'dashed !important' }}>
                            <i className="fas fa-file-upload fa-2x text-light mb-2"></i>
                            <p className="mb-0 text-muted small"></p>
                        </div>
                    )}

                    <ListGroup variant="flush" className="mt-2">
                        {fileList.map((file, index) => (
                            <ListGroup.Item key={index} className="px-0 py-2 d-flex align-items-center border-bottom-0">
                                <div className="mr-3">
                                    <i className={`fas ${getFileIcon(file.name || file.filePath)} fa-lg`}></i>
                                </div>
                                <div className="flex-grow-1 overflow-hidden">
                                    <div className="text-dark text-truncate small font-weight-bold" title={file.name || file.filePath}>
                                        {file.name || (file.filePath ? file.filePath.split('/').pop().replace(/_\d{14}(?=\.\w+$|$)/, '') : 'Tệp tin không tên')}
                                    </div>
                                    <div className="d-flex small align-items-center text-muted">
                                        <a href={BaseGatewayImage + file.filePath} target="_blank" rel="noreferrer" className="text-muted mr-3">
                                            <i className="fas fa-external-link-alt mr-1"></i> Xem trực tiếp
                                        </a>
                                    </div>
                                </div>
                                <div className="ml-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger border-0 rounded-circle"
                                        onClick={() => removeFile(index)}
                                        title="Xóa tệp"
                                        style={{ width: '32px', height: '32px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        Xóa
                                    </button>
                                </div>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>

                    {isInvalid && (
                        <div className="text-danger small mt-2">
                            <i className="fas fa-exclamation-circle mr-1"></i> {errorsValue}
                        </div>
                    )}
                </Card.Body>
            </Card>

            <style dangerouslySetInnerHTML={{
                __html: `
                .document-input-container .list-group-item {
                    transition: all 0.2s;
                    border-radius: 4px;
                }
                .document-input-container .list-group-item:hover {
                    background-color: #f8f9fa;
                }
                .document-input-container .btn-light:hover {
                    background-color: #ffeef0;
                }
            `}} />
        </div>
    );
};

DocumentInput.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    multiple: PropTypes.bool,
    isInvalid: PropTypes.bool,
    errorsValue: PropTypes.string,
    onChangeProp: PropTypes.func,
    onFileUploaded: PropTypes.func,
    onFilesChange: PropTypes.func,
    labelTitle: PropTypes.node,
    labelSubTitle: PropTypes.node,
    maxSizeMB: PropTypes.number,
    accept: PropTypes.string
};

DocumentInput.defaultProps = {
    multiple: false,
    value: ''
};

export default DocumentInput;
